import { createContext, ReactNode, useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectUser } from 'app/store/userSlice';
import { useGetStagesQuery } from '@/app/api/stagesApiSlice';
import { useGetRolesQuery } from '@/app/api/rolesApiSlice';
import { useGetServiceTypesQuery } from '@/app/api/servicesApiSlice';
import { Stage } from '../shared-components/Types/Stages';
import { Paged } from '../shared-components/Types/Paged';
import { Role } from '../shared-components/Types/Role';
import { useGetLicenseesQuery } from '@/app/api/licenseesApiSlice';
import { Licensee } from 'app/shared-components/Types/Licensees';
import { ResearchOption } from 'app/shared-components/Types/Research';
import { useGetResearchOptionsQuery } from '../api/researchApiSlice';
import { EnumValue, useGetDwellingTypesQuery } from '@/app/api/messagingRulesApiSlice';

export interface ServiceItem {
  id: number;
  name: string;
}

interface GlobalContextProps {
  stages: Stage[];
  closedStages: Stage[];
  filterStages: Stage[];
  licensees: Licensee[];
  roles: Paged<Role> | undefined;
  serviceTypes: Paged<ServiceItem> | undefined;
  dwellingTypes: EnumValue[];
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  researchOptions: ResearchOption[];
}

const defaultValues: GlobalContextProps = {
  stages: [],
  closedStages: [],
  filterStages: [],
  licensees: [],
  roles: { items: [], currentPage: 0, resultsPerPage: 0, totalPages: 0, totalResults: 0 },
  serviceTypes: { items: [], currentPage: 0, resultsPerPage: 0, totalPages: 0, totalResults: 0 },
  dwellingTypes: [],
  isLoading: true,
  isSuccess: false,
  isError: false,
  researchOptions: [],
};

const GlobalContext = createContext<GlobalContextProps>(defaultValues);

export const GlobalDataProvider = ({ children }: { children: ReactNode }) => {
  const user = useSelector(selectUser);

  const stagesQuery = useGetStagesQuery(undefined, { skip: !user.id });
  const rolesQuery = useGetRolesQuery(undefined, { skip: !user.id });
  const servicesQuery = useGetServiceTypesQuery(undefined, { skip: !user.id });
  const licenseesQuery = useGetLicenseesQuery(undefined, { skip: !user.id });
  const researchOptionsQuery = useGetResearchOptionsQuery(undefined, { skip: !user.id });
  const dwellingTypesQuery = useGetDwellingTypesQuery(undefined, { skip: !user.id });

  const stages =
    stagesQuery.data?.items.filter((stage) => !stage.isArchived && !stage.isCanceled) ?? [];
  const closedStages =
    stagesQuery.data?.items.filter((stage) => stage.isArchived || stage.isCanceled) ?? [];

  const licensees = licenseesQuery.data?.items ?? [];
  const researchOptions = researchOptionsQuery.data ?? [];
  const dwellingTypes = dwellingTypesQuery.data ?? [];

  const filterStages = stagesQuery.data?.items.filter((stage) => !stage.isArchived) ?? [];

  const providerValue = useMemo(() => {
    return {
      stages,
      closedStages,
      filterStages,
      licensees,
      roles: rolesQuery.data,
      serviceTypes: servicesQuery.data,
      dwellingTypes,
      researchOptions,
      isLoading:
        stagesQuery.isLoading ||
        rolesQuery.isLoading ||
        servicesQuery.isLoading ||
        licenseesQuery.isLoading ||
        researchOptionsQuery.isLoading ||
        dwellingTypesQuery.isLoading,
      isSuccess:
        stagesQuery.isSuccess &&
        rolesQuery.isSuccess &&
        servicesQuery.isSuccess &&
        licenseesQuery.isSuccess &&
        researchOptionsQuery.isSuccess &&
        dwellingTypesQuery.isSuccess,
      isError:
        stagesQuery.isError ||
        rolesQuery.isError ||
        servicesQuery.isError ||
        licenseesQuery.isError ||
        researchOptionsQuery.isError ||
        dwellingTypesQuery.isError,
    };
  }, [
    stagesQuery,
    rolesQuery,
    servicesQuery,
    licenseesQuery,
    researchOptionsQuery,
    dwellingTypesQuery,
  ]);

  return <GlobalContext.Provider value={providerValue}>{children}</GlobalContext.Provider>;
};

// Custom Hook for GlobalData Access
export const useGlobalData = () => {
  const context = useContext(GlobalContext);
  if (context === undefined) {
    throw new Error('useGlobalData must be used within a GlobalDataProvider');
  }
  return context;
};
